import { useTranslation } from 'react-i18next';
import PriorityBadge from './PriorityBadge';
import ConfidenceIndicator from './ConfidenceIndicator';
import CategoryBadge from './CategoryBadge';
import StatusBadge from './StatusBadge';

export interface Signal {
  id: string;
  summary: string;
  category: string;
  urgency: 'critical' | 'high' | 'medium' | 'low';
  confidence: number;
  status: string;
  action_required?: boolean;
  source_chat_name?: string;
  created_at: string;
}

interface SignalCardProps {
  signal: Signal;
  onConfirm: (id: string) => void;
  onDismiss: (id: string) => void;
  onExpand: (id: string) => void;
  compact?: boolean;
}

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function SignalCard({
  signal,
  onConfirm,
  onDismiss,
  onExpand,
  compact = false,
}: SignalCardProps) {
  const { t } = useTranslation();

  return (
    <div
      className={`rounded-lg border bg-white shadow-sm transition-shadow hover:shadow-md ${
        signal.action_required
          ? 'border-orange-300 bg-orange-50/30'
          : 'border-gray-200'
      } ${compact ? 'p-3' : 'p-4'}`}
    >
      {/* Top row: category + urgency + status */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <CategoryBadge category={signal.category} />
        <PriorityBadge level={signal.urgency} />
        <StatusBadge status={signal.status} type="signal" />
        {signal.action_required && (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-orange-600">
            {t('signals.actionRequired')}
          </span>
        )}
      </div>

      {/* Summary */}
      <p
        className={`text-sm text-gray-900 leading-snug ${
          compact ? 'line-clamp-1' : 'line-clamp-2'
        } mb-2`}
      >
        {signal.summary}
      </p>

      {/* Confidence bar */}
      <div className="mb-2 max-w-[140px]">
        <ConfidenceIndicator confidence={signal.confidence} />
      </div>

      {/* Meta row */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-3">
        {signal.source_chat_name && (
          <span className="truncate max-w-[140px]">
            {'💬'} {signal.source_chat_name}
          </span>
        )}
        <span>{relativeTime(signal.created_at)}</span>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 border-t border-gray-100 pt-2">
        <button
          onClick={() => onConfirm(signal.id)}
          className="inline-flex items-center gap-1 rounded-md bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-100 transition-colors"
          title={t('common.confirm')}
        >
          {'✓'} {t('common.confirm')}
        </button>
        <button
          onClick={() => onDismiss(signal.id)}
          className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100 transition-colors"
          title={t('common.dismiss')}
        >
          {'✗'} {t('common.dismiss')}
        </button>
        <button
          data-testid="signal-expand"
          aria-label={t('common.expand', 'Expand')}
          onClick={() => onExpand(signal.id)}
          className="inline-flex items-center gap-1 rounded-md bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors ltr:ml-auto rtl:mr-auto"
        >
          {'↗'}
        </button>
      </div>
    </div>
  );
}
